export function createQualityDisplayRowService(deps) {
    const {
        PANEL_TYPE,
        ROW_ID_FIELD,
        DEMERIT_NAME_FLG,
        DEMERIT_HUDA_FLG,
        isEmpty,
        DicVal,
        canonicalPanelType,
        normalizeColumnIdUpper,
        normalizeKakousijiUiString,
        normalizeColorCd,
        getPanelType,
        getQualityPanelFieldMap,
        getQualityInvalidField,
        qualityPanelContentColumnIds,
        isKakousijiSavePayloadUiOnlyField,
        readRowValueIgnoreCase,
        normalizeDemeritRow,
        normalizeDemeritRowForSave,
        applyWashMarkDisplayFields,
        isQualityPanelType
    } = deps;

    // invalid 列を見て表示対象外 row か判定します。
    function isInvalidDisplayRow(row, panelType) {
        const invalidField = getQualityInvalidField(panelType);
        if (invalidField === "") return false;
        const value = String(readRowValueIgnoreCase(row, invalidField) ?? "").trim().toLowerCase();
        return value === "1" || value === "true";
    }

    // 品質表示 panel だけ invalid row を除外します。
    function filterInvalidRowsForPanel(panelInfo, rows) {
        if (!isQualityPanelType(getPanelType(panelInfo))) return rows;
        return (Array.isArray(rows) ? rows : []).filter((row) => !isInvalidDisplayRow(row, getPanelType(panelInfo)));
    }

    // 画面表示用 row を panel type ごとに正規化します。
    function normalizeQualityDisplayRowForPanel(row, panelType) {
        const type = canonicalPanelType(panelType);
        if (type === PANEL_TYPE.DEMERIT) return normalizeDemeritRow(row);
        if (type === PANEL_TYPE.WASH) return applyWashMarkDisplayFields(row);
        return row;
    }

    // 保存不要な __QUALITY メタ項目を落とします。
    function stripQualityDisplayMetaFields(row) {
        if (isEmpty(row) || typeof row !== "object") return row;
        const out = { ...row };
        Object.keys(out).forEach((key) => {
            if (normalizeColumnIdUpper(key).startsWith("__QUALITY")) delete out[key];
        });
        return out;
    }

    // 保存前の品質表示 row を正規化します。
    function normalizeQualityDisplayRowForSave(row, panelType) {
        const type = canonicalPanelType(panelType);
        const cleanRow = stripQualityDisplayMetaFields(row);
        if (type === PANEL_TYPE.DEMERIT) return normalizeDemeritRowForSave(cleanRow);
        return cleanRow;
    }

    // row が保存対象として意味を持つか判定します。
    function isQualityDisplayRowMeaningful(row, panelType) {
        if (isEmpty(row) || typeof row !== "object") return false;
        const type = canonicalPanelType(panelType);
        const map = getQualityPanelFieldMap(type) || {};
        const normalized = normalizeQualityDisplayRowForSave(row, type);
        const excluded = new Set([
            ROW_ID_FIELD,
            "INDEX",
            "GRIP",
            "EXPAND",
            "ROW_KIND",
            String(DicVal(map, "no", "") ?? ""),
            String(DicVal(map, "item", "") ?? ""),
            String(DicVal(map, "group", "") ?? ""),
            String(DicVal(map, "groupFallback", "") ?? ""),
            String(DicVal(map, "color", "") ?? ""),
            String(DicVal(map, "order", "") ?? ""),
            String(DicVal(map, "invalid", "") ?? "")
        ].map((fieldId) => normalizeColumnIdUpper(fieldId)).filter((fieldId) => fieldId !== ""));
        const contentColumnIds = qualityPanelContentColumnIds(type);
        const candidateKeys = contentColumnIds.length !== 0 ? contentColumnIds : Object.keys(normalized || {});
        return candidateKeys.some((key) => {
            const cid = normalizeColumnIdUpper(key);
            if (cid === "" || excluded.has(cid)) return false;
            if (isKakousijiSavePayloadUiOnlyField(key, type)) return false;
            const value = normalizeKakousijiUiString(readRowValueIgnoreCase(normalized, key));
            if (value === "") return false;
            if (type === PANEL_TYPE.DEMERIT && (cid === normalizeColumnIdUpper(DEMERIT_NAME_FLG) || cid === normalizeColumnIdUpper(DEMERIT_HUDA_FLG)) && value === "0") return false;
            return true;
        });
    }

    // 表示 row が持つ対象 color 一覧を返します。
    function readQualityDisplayTargetColors(row) {
        const colors = DicVal(row, "__QUALITY_TARGET_COLORS", null);
        return Array.isArray(colors)
            ? colors.map((value) => normalizeColorCd(value)).filter((value) => value !== "")
            : [];
    }

    return {
        isInvalidDisplayRow,
        filterInvalidRowsForPanel,
        normalizeQualityDisplayRowForPanel,
        stripQualityDisplayMetaFields,
        normalizeQualityDisplayRowForSave,
        isQualityDisplayRowMeaningful,
        readQualityDisplayTargetColors
    };
}
